import { Collapse, Stack } from '@mui/material';
import { RefObject, useEffect } from 'react';
import { useLocalStorage } from 'react-use';

import { LOCAL_STORAGE_KEYS } from '@/constants/localStorageKeys';
import { useResizableRightPanel } from '@/hooks/useResizableRightPanel';
import { ChatSideContentPane } from '@/modules/common/Chat/ChatSideContent/ChatSideContentPane';
import { useChatSideContentContext } from '@/modules/common/Chat/ChatSideContent/context/chatSideContent.context';
import { THEME_SHADOWS } from '@/styles/themes/common';
import { COLORS } from '@/styles/tokens/colors';
import { OVERLAY_SM } from '@/styles/tokens/panelWidths';
import { zIndexes } from '@/styles/zIndexes';

import { HouseholdChatShell } from './HouseholdChatShell';
import { ResizeHandle } from './ResizeHandle';
import {
  SIDE_CONTENT_DEFAULT_WIDTH,
  SIDE_CONTENT_MAX_WIDTH,
  SIDE_CONTENT_MIN_WIDTH,
} from './sideContentPanel.constants';

const MIN_CHAT_WIDTH = 300;
const MAX_CHAT_WIDTH = 640;

interface HouseholdChatDockedProps {
  /**
   * The content area below the app bar (page column + this pane). The resize
   * hooks measure this container to translate drag positions into widths.
   */
  containerRef: RefObject<HTMLDivElement | null>;
  isOpen: boolean;
}

/**
 * The docked host: renders the chat shell as a resizable sidebar column in
 * the household layout's right-sidebar slot. Owns both resizable widths —
 * the handle laid over the column's left edge drags the chat column (and the
 * width persists across sessions), while the side content panel's own handle
 * resizes just the panel. The panel floats over the page content beside the
 * column, so opening it never squeezes the page.
 */
export function HouseholdChatDocked({
  containerRef,
  isOpen,
}: HouseholdChatDockedProps) {
  const [storedWidth, setStoredWidth] = useLocalStorage<number>(
    LOCAL_STORAGE_KEYS.HOUSEHOLD_CHAT_WIDTH,
    OVERLAY_SM
  );
  const {
    handleMouseDown: handleChatColumnResize,
    panelWidth: chatColumnWidth,
    resizeRef: chatColumnResizeRef,
  } = useResizableRightPanel({
    containerRef,
    defaultWidth: storedWidth ?? OVERLAY_SM,
    maxWidth: MAX_CHAT_WIDTH,
    minWidth: MIN_CHAT_WIDTH,
  });

  useEffect(() => {
    void setStoredWidth(chatColumnWidth);
  }, [chatColumnWidth, setStoredWidth]);

  const { sideContent } = useChatSideContentContext();
  const hasSideContent = Boolean(sideContent);

  const {
    handleMouseDown: handleSideContentResize,
    panelWidth: sideContentWidth,
    resizeRef: sideContentResizeRef,
  } = useResizableRightPanel({
    containerRef,
    defaultWidth: SIDE_CONTENT_DEFAULT_WIDTH,
    maxWidth: SIDE_CONTENT_MAX_WIDTH,
    minWidth: SIDE_CONTENT_MIN_WIDTH,
    // The panel's right edge is the chat column's left edge, not the
    // container's right edge: offset drag math by the chat column.
    rightOffset: chatColumnWidth,
  });

  return (
    <Collapse
      data-testid="household-chat-docked"
      in={isOpen}
      orientation="horizontal"
      sx={{ height: '100%' }}
      unmountOnExit
    >
      <Stack
        sx={{
          backgroundColor: COLORS.PRIMITIVES.WHITE,
          borderLeft: `solid 1px ${COLORS.GRAY[200]}`,
          height: '100%',
          position: 'relative',
          width: `${chatColumnWidth}px`,
        }}
      >
        {hasSideContent && (
          <Stack
            sx={{
              // Anchored to the docked column's left edge (right: 100% of this
              // relative Stack), the panel floats over the page content instead
              // of pushing it, and follows the column as its width is dragged.
              bottom: 0,
              position: 'absolute',
              right: '100%',
              top: 0,
              width: `${sideContentWidth}px`,
              // The panel covers this Stack's left border, so it carries the
              // divider between itself and the chat column on its own right
              // edge.
              borderRight: `solid 1px ${COLORS.GRAY[200]}`,
              boxShadow: THEME_SHADOWS.lg,
              // Above the page content it floats over, below full-screen
              // modals/trowsers — same layer as the chat overlay drawer.
              zIndex: zIndexes.MENU + 1,
            }}
          >
            <ResizeHandle
              onMouseDown={handleSideContentResize}
              resizeRef={sideContentResizeRef}
              // Laid over the panel's left edge (instead of taking flex
              // space) so the pane header and its bottom border span the full
              // panel width, like the chat column's handle below.
              sx={{
                bottom: 0,
                left: 0,
                position: 'absolute',
                top: 0,
                zIndex: 1,
              }}
            />
            <ChatSideContentPane />
          </Stack>
        )}
        <ResizeHandle
          onMouseDown={handleChatColumnResize}
          resizeRef={chatColumnResizeRef}
          // Laid over the column's left edge (instead of taking flex space) so
          // the chat header and its bottom border span the full column width.
          sx={{ bottom: 0, left: 0, position: 'absolute', top: 0, zIndex: 1 }}
        />
        <HouseholdChatShell />
      </Stack>
    </Collapse>
  );
}
